import * as MUI from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { Fragment, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Date from "../../../components/form/Date";
import GenderSelect from "../../../components/form/GenderSelect";
import UserSelector from "../../../components/form/UserSelector";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { UserContext } from "../../../contexts/UserProvider";
import { CHANGE_PET_OWNER } from "../../../enums/PermissionTitles";
import useUser from "../../../hooks/useUser";
import catBreeds from "../../../features/master/species/breeds/CatBreeds.json";
import dogBreeds from "../../../features/master/species/breeds/DogBreeds.json";
import dayjs from "dayjs";
import SimpleAlert from "../../../components/display/SimpleAlert";
import CharCountField from "../../../components/form/CharCountField";
import { generalMaxDesc } from "../../services/form/ServiceForm";
import { BloodDonorSwitch } from "../../../components/form/BloodDonorSwitch";

const petFields = {
  name: "name",
  date: "birthdate",
  chip: "microchip",
  passport: "passport",
  gender: "gender",
  desc: "description",
  aggression: "aggression",
  bloodDonor: "bloodDonor",
  bloodType: "bloodType",
};

export default function EditPetForm({ mutationKey }) {
  const methods = useFormContext();
  const { errors } = methods.formState;
  const { roles } = useUser();
  const client = useQueryClient();
  const pet = client.getQueryData([mutationKey]);
  const { t } = useTranslation();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const {
    getAutoCompleteUsers,
    isLoading: userLoading,
    error: errorFetchingUsers,
  } = React.useContext(UserContext);

  let parsedDogBreeds = [];
  let parsedCatBreeds = [];

  dogBreeds.dog_breeds.forEach((breed) => parsedDogBreeds.push(breed));
  parsedDogBreeds.sort((a, b) => t(a).localeCompare(t(b)));

  catBreeds.cat_breeds.forEach((breed) => parsedCatBreeds.push(breed));
  parsedCatBreeds.sort((a, b) => t(a).localeCompare(t(b)));

  const spacing =
    pet?.type?.breed === null || pet?.type?.breed === "noBreed" ? 12 : 6;

  function getBreedList() {
    if (pet?.type?.breed === "catBreeds") {
      return parsedCatBreeds;
    } else if (pet?.type?.breed === "dogBreeds") {
      return parsedDogBreeds;
    }

    return [];
  }

  return (
    <Fragment>
      <MUI.Grid container spacing={2}>
        <MUI.Grid item xs={12} lg={spacing} md={6}>
          <MUI.FormControl fullWidth>
            <MUI.TextField
              value={methods.watch(petFields.name) || ""}
              inputProps={{
                maxLength: 120,
                "data-testid": "pet.edit-form.name-input",
              }}
              key={petFields.name}
              label={t("Name")}
              {...methods.register(petFields.name, {
                required: t("EmptyField"),
              })}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          </MUI.FormControl>
        </MUI.Grid>
        {pet?.type?.breed === "catBreeds" ||
        pet?.type?.breed === "dogBreeds" ? (
          <MUI.Grid item xs={12} lg={6} md={6}>
            <Controller
              name={"breed"}
              render={({ field, fieldState: { error } }) => (
                <MUI.FormControl fullWidth error={!!error}>
                  <MUI.Autocomplete
                    options={getBreedList()}
                    noOptionsText={t("noSelectableBreed")}
                    getOptionLabel={(option) => t(option)}
                    defaultValue={getBreedList()?.find(
                      (petBreed) => String(petBreed) === String(pet?.breed),
                    )}
                    onChange={(event, value) => field.onChange(value || null)}
                    renderInput={(params) => (
                      <MUI.TextField
                        {...params}
                        label={t("breed")}
                        error={!!error}
                        helperText={error?.message}
                        inputProps={{
                          ...params.inputProps,
                          "data-testid": "pet.edit-form.breed-select",
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>{t(option)}</li>
                    )}
                    fullWidth
                    clearText={t("Clear")}
                    closeText={t("Close")}
                    openText={t("Open")}
                  />
                </MUI.FormControl>
              )}
            />
          </MUI.Grid>
        ) : null}
        <MUI.Grid item xs={6}>
          <GenderSelect
            name={petFields.gender}
            testId="pet.edit-form.gender-select"
          />
        </MUI.Grid>
        <MUI.Grid item xs={6}>
          <Date
            name={petFields.date}
            value={pet?.birthdate ? dayjs(pet?.birthdate, "DD/MM/YYYY") : null}
            rulesValue={{ required: false }}
            control={methods.control}
            errors={errors}
            label={t("PetDobOptional")}
            openTo="year"
            testId="pet.edit-form.date-input"
          />
        </MUI.Grid>
        <MUI.Grid item xs={12} lg={6} md={6}>
          <MUI.FormControl fullWidth>
            <MUI.TextField
              value={methods.watch(petFields.passport) || ""}
              inputProps={{
                maxLength: 30,
                "data-testid": "pet.edit-form.passport-input",
              }}
              key={petFields.passport}
              label={t("Passport")}
              {...methods.register(petFields.passport)}
            />
          </MUI.FormControl>
        </MUI.Grid>
        <MUI.Grid item xs={12} lg={6} md={6}>
          <MUI.FormControl fullWidth>
            <MUI.TextField
              value={methods.watch(petFields.chip) || ""}
              inputProps={{
                maxLength: 30,
                "data-testid": "pet.edit-form.microchip-input",
              }}
              key={petFields.chip}
              label={t("Microchip")}
              {...methods.register(petFields.chip)}
            />
          </MUI.FormControl>
        </MUI.Grid>
        {isPermissionAllowed(CHANGE_PET_OWNER) && !roles.user && (
          <>
            {userLoading ? (
              <MUI.Grid item xs={12} md={6} lg={12}>
                <MUI.LinearProgress />
              </MUI.Grid>
            ) : errorFetchingUsers ? (
              <MUI.Grid item xs={12} md={6} lg={12}>
                <SimpleAlert
                  message={"ErrorFetchingUsers"}
                  severity={"error"}
                />
              </MUI.Grid>
            ) : (
              <MUI.Grid item xs={12} md={6} lg={12}>
                <UserSelector
                  users={getAutoCompleteUsers()}
                  showNewUserForm={false}
                  testId="pet.edit-form.user-selector"
                />
              </MUI.Grid>
            )}
          </>
        )}

        <MUI.Grid item xs={12}>
          <BloodDonorSwitch
            bloodDonorKey={petFields.bloodDonor}
            bloodTypeKey={petFields.bloodType}
            testIdPrefix="pet.edit-form"
          />
        </MUI.Grid>

        <MUI.Grid item xs={12} lg={12} md={12}>
          <MUI.FormControl fullWidth>
            {!roles.user && (
              <CharCountField
                maxChar={255}
                maxSize={1}
                label={t("Aggression")}
                {...methods.register(petFields.aggression)}
                rows={2}
                testId="pet.edit-form.aggression-input"
              />
            )}
          </MUI.FormControl>
        </MUI.Grid>

        <MUI.Grid item xs={12}>
          <MUI.FormControl fullWidth>
            <CharCountField
              label={t("Description")}
              {...methods.register(petFields.desc)}
              multiline
              rows={10}
              testId="pet.edit-form.description-input"
            />
          </MUI.FormControl>
        </MUI.Grid>
      </MUI.Grid>
    </Fragment>
  );
}
